import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Truck, ExternalLink, Check, X, Loader2, ShieldCheck,
  Sparkles, Link as LinkIcon, AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ShipmentConfirmDialog — dialog seller per confermare la spedizione.
 * Sostituisce il vecchio dialog "Conferma invio pacco" con:
 *   1) Selezione visuale del corriere (grid di card animate)
 *   2) Campo tracking obbligatorio
 *   3) Branch dinamico "Altro corriere" con URL validato (HTTPS + dominio)
 *   4) Feedback live sulla validità dell'URL
 *   5) Animazioni framer-motion, empty states curati
 *
 * onSuccess(shipment) — callback dopo creazione riuscita (per aggiornare la UI del parent)
 */
const validateHttpsUrl = (url) => {
  if (!url) return { valid: false, error: 'Inserisci un URL' };
  const trimmed = url.trim();
  if (!/^https:\/\//i.test(trimmed)) return { valid: false, error: 'Deve iniziare con https://' };
  try {
    const u = new URL(trimmed);
    if (!u.hostname || !u.hostname.includes('.')) return { valid: false, error: 'Dominio non valido' };
    return { valid: true, error: null };
  } catch {
    return { valid: false, error: 'URL non valido' };
  }
};

const carrierLogo = (code) => {
  // Placeholder: colore/iniziali. Sostituibile con logo_url dal catalogo.
  const map = {
    gls: { bg: 'bg-yellow-100', ring: 'ring-yellow-400', text: 'text-yellow-800', label: 'GLS' },
    brt: { bg: 'bg-red-100', ring: 'ring-red-400', text: 'text-red-800', label: 'BRT' },
    poste: { bg: 'bg-blue-100', ring: 'ring-blue-400', text: 'text-blue-800', label: 'PT' },
    dhl: { bg: 'bg-orange-100', ring: 'ring-orange-400', text: 'text-orange-800', label: 'DHL' },
    ups: { bg: 'bg-amber-100', ring: 'ring-amber-500', text: 'text-amber-900', label: 'UPS' },
    fedex: { bg: 'bg-purple-100', ring: 'ring-purple-400', text: 'text-purple-800', label: 'FDX' },
    inpost: { bg: 'bg-yellow-50', ring: 'ring-yellow-300', text: 'text-yellow-900', label: 'IP' },
    other: { bg: 'bg-slate-100', ring: 'ring-slate-400', text: 'text-slate-700', label: '···' },
  };
  return map[code] || { bg: 'bg-teal-100', ring: 'ring-teal-400', text: 'text-teal-800', label: code?.slice(0,3).toUpperCase() };
};

export const ShipmentConfirmDialog = ({ open, onOpenChange, order, sellerId, onSuccess }) => {
  const [carriers, setCarriers] = useState([]);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [selectedCode, setSelectedCode] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [customCarrierName, setCustomCarrierName] = useState('');
  const [customTrackingUrl, setCustomTrackingUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset al riapertura
  useEffect(() => {
    if (open) {
      setSelectedCode('');
      setTrackingNumber('');
      setCustomCarrierName('');
      setCustomTrackingUrl('');
    }
  }, [open]);

  // Carica catalogo corrieri
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingCarriers(true);
      try {
        const r = await fetch(`${API_URL}/api/carriers`);
        if (!r.ok) throw new Error('Impossibile caricare corrieri');
        const data = await r.json();
        if (!cancelled) setCarriers(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) toast.error('Errore caricamento corrieri');
      } finally {
        if (!cancelled) setLoadingCarriers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const isOther = selectedCode === 'other';
  const urlCheck = useMemo(() => (isOther ? validateHttpsUrl(customTrackingUrl) : { valid: true, error: null }), [isOther, customTrackingUrl]);

  const canSubmit = useMemo(() => {
    if (!selectedCode || !trackingNumber.trim()) return false;
    if (isOther) {
      if (!customCarrierName.trim()) return false;
      if (!urlCheck.valid) return false;
    }
    return true;
  }, [selectedCode, trackingNumber, isOther, customCarrierName, urlCheck]);

  const submit = async () => {
    if (!canSubmit || !order || !sellerId) return;
    setSubmitting(true);
    try {
      const payload = {
        order_id: order.id,
        seller_id: sellerId,
        carrier_code: selectedCode,
        tracking_number: trackingNumber.trim(),
      };
      if (isOther) {
        payload.custom_carrier_name = customCarrierName.trim();
        payload.custom_tracking_url = customTrackingUrl.trim();
      }
      const r = await fetch(`${API_URL}/api/shipments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Errore creazione spedizione');
      toast.success('Spedizione registrata!');
      onSuccess?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Errore imprevisto');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="shipment-confirm-dialog">
        {/* Header con gradient e badge */}
        <div className="relative overflow-hidden bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-600 p-6 text-white">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, white 0.5px, transparent 0.5px)', backgroundSize: '16px 16px' }} />
          <div className="relative flex items-center gap-3">
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', damping: 12 }}
              className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg"
            >
              <Package className="w-6 h-6" />
            </motion.div>
            <div>
              <DialogHeader>
                <DialogTitle className="text-white text-xl font-bold">Conferma spedizione</DialogTitle>
              </DialogHeader>
              <p className="text-teal-50 text-sm mt-0.5">
                Ordine #{order?.order_number || order?.id?.slice(0, 8).toUpperCase()}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Step 1: Selezione corriere */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 font-semibold text-xs flex items-center justify-center">1</div>
              <Label className="text-sm font-semibold text-slate-800">Corriere</Label>
              <span className="text-red-500">*</span>
            </div>
            {loadingCarriers ? (
              <div className="grid grid-cols-4 gap-3">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-20 rounded-xl bg-slate-100 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {carriers.map((c, idx) => {
                  const logo = carrierLogo(c.code);
                  const isSelected = selectedCode === c.code;
                  return (
                    <motion.button
                      key={c.code}
                      type="button"
                      onClick={() => setSelectedCode(c.code)}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      className={`relative flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-teal-500 bg-teal-50/50 shadow-md'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid={`carrier-option-${c.code}`}
                    >
                      {isSelected && (
                        <motion.div
                          layoutId="carrier-check"
                          className="absolute -top-2 -right-2 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center shadow"
                        >
                          <Check className="w-3 h-3 text-white" />
                        </motion.div>
                      )}
                      <div className={`w-10 h-10 rounded-lg ${logo.bg} ring-1 ${logo.ring} flex items-center justify-center font-bold text-xs ${logo.text}`}>
                        {logo.label}
                      </div>
                      <div className="text-xs font-medium text-slate-700 text-center leading-tight">
                        {c.name}
                      </div>
                      {c.carrier_type === 'INTEGRATED' && (
                        <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2">
                          <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-500 text-white text-[9px] font-bold rounded-full shadow">
                            <Sparkles className="w-2 h-2" />
                            AUTO
                          </div>
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step 2: Tracking number */}
          <AnimatePresence>
            {selectedCode && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 font-semibold text-xs flex items-center justify-center">2</div>
                  <Label htmlFor="tracking-number" className="text-sm font-semibold text-slate-800">Numero tracking</Label>
                  <span className="text-red-500">*</span>
                </div>
                <div className="relative">
                  <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="tracking-number"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Es. 1Z999AA10123456784"
                    className="pl-10 font-mono"
                    data-testid="tracking-number-input"
                    autoFocus
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Step 3: campi custom per "Altro corriere" */}
          <AnimatePresence>
            {isOther && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="space-y-4 p-4 rounded-xl bg-slate-50 border border-slate-200"
              >
                <div>
                  <Label htmlFor="custom-carrier-name" className="text-sm font-semibold text-slate-800">
                    Nome corriere <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="custom-carrier-name"
                    value={customCarrierName}
                    onChange={(e) => setCustomCarrierName(e.target.value)}
                    placeholder="Es. Nexive, Mail Boxes, MRW..."
                    className="mt-1.5"
                    data-testid="custom-carrier-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="custom-tracking-url" className="text-sm font-semibold text-slate-800">
                    Sito per il tracking <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative mt-1.5">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="custom-tracking-url"
                      type="url"
                      value={customTrackingUrl}
                      onChange={(e) => setCustomTrackingUrl(e.target.value)}
                      placeholder="https://tracking.corriere.it/..."
                      className={`pl-10 pr-10 ${customTrackingUrl && !urlCheck.valid ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                      data-testid="custom-tracking-url-input"
                    />
                    {customTrackingUrl && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {urlCheck.valid ? (
                          <Check className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <X className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {customTrackingUrl && !urlCheck.valid && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {urlCheck.error}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Solo HTTPS e domini validi
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer info per corrieri integrati */}
          <AnimatePresence>
            {selectedCode && selectedCode !== 'other' && (
              (() => {
                const c = carriers.find((x) => x.code === selectedCode);
                if (c?.carrier_type === 'INTEGRATED') {
                  return (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200"
                    >
                      <Sparkles className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-emerald-800 leading-relaxed">
                        <strong>Tracking automatico:</strong> gli aggiornamenti di consegna arriveranno direttamente da {c.name} e verranno mostrati al cliente in tempo reale.
                      </p>
                    </motion.div>
                  );
                }
                return null;
              })()
            )}
          </AnimatePresence>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="cancel-shipment-btn"
            >
              Annulla
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white font-semibold"
              data-testid="confirm-shipment-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Registrazione...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Conferma spedizione</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShipmentConfirmDialog;
